import React, {
  useCallback,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';

import AnnouncementContext from '../../../../../context/AnnouncementContext';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import AnnouncementComment from '../../../../../Model/AnnouncementComment';
import { PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import LinkifiedText from '../../../../../../components/LinkifiedText';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../../components/ConfirmDialog';

import {
  HeaderContainer,
  StyledCard,
  StyledUserProfileCard,
  CommentText,
  TopContainer,
  BottomContainer,
} from './styles';
import texts from './texts.json';

const COMMENT_LENGTH_IN_CONFIRM_DIALOG = 70;

const NotificationItem = ({ comment }) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { updateCommentAndAnnouncementStateById } = useContext(AnnouncementContext);

  const {
    userName,
    userAvatarUrl,
    comment: commentText,
    userId,
    displayDate,
  } = comment;

  const markAsRead = useCallback(async () => {
    setIsLoading(true);
    await comment.markAsRead();
    if (isComponentMountedRef.current) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
    showToast(texts.markAsReadSuccess);
  }, [
    comment,
    isComponentMountedRef,
    showToast,
  ]);

  const onViewComment = useCallback(async () => {
    try {
      await updateCommentAndAnnouncementStateById(comment.announcementId, comment.id);
      await comment.markAsRead();
    } catch (error) {
      showToast(format(texts.commentViewFailed, { error: error.message }), { error: true });
    }
  }, [
    comment,
    updateCommentAndAnnouncementStateById,
    showToast,
  ]);

  return (
    <StyledCard>
      <TopContainer>
        <HeaderContainer>
          <StyledUserProfileCard
            dataRow={{
              userName,
              userAvatar: userAvatarUrl,
            }}
            clientId={userId}
            hideActions
            subTitle={moment(displayDate.toDate()).fromNow()}
          />
          <PrimaryButton
            variant="info"
            size="medium"
            onClick={() => setIsConfirmDialogOpen(true)}
          >
            {texts.markAsRead}
          </PrimaryButton>
        </HeaderContainer>
        <CommentText><LinkifiedText text={commentText} /></CommentText>
      </TopContainer>
      <BottomContainer>
        <PrimaryButton
          variant="light"
          size="medium"
          onClick={onViewComment}
        >
          {texts.viewComment}
        </PrimaryButton>
      </BottomContainer>
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onCancel={() => setIsConfirmDialogOpen(false)}
        onConfirm={markAsRead}
        dialogTexts={{
          title: texts.markAsRead,
          content: format(texts.markAsReadDescription, {
            comment: commentText.length > COMMENT_LENGTH_IN_CONFIRM_DIALOG
              ? `${commentText.slice(0, COMMENT_LENGTH_IN_CONFIRM_DIALOG)}…`
              : commentText,
          }),
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
    </StyledCard>
  );
};

NotificationItem.propTypes = {
  comment: PropTypes.instanceOf(AnnouncementComment).isRequired,
};

export default NotificationItem;
